<template>
  <b-modal
    :active="displayModal"
    has-modal-card
    @close="$emit('close')"
    :can-cancel="['escape', 'x', 'outside']"
    :width="1000"
  >
    <div class="modal-card" style="width: 1000px">
      <form @submit.prevent="validateBeforeSubmit">
        <header class="modal-card-head">
          <p class="modal-card-title">{{ modalTitle }}</p>
        </header>
        <section class="modal-card-body">
          <b-notification :closable="false">
            {{ $t('compCarbonFormsVehiclesModal.info-1') }}
            <br />
            {{ $t('compCarbonFormsVehiclesModal.info-2') }}
          </b-notification>
          <b-notification :closable="false">
            <b-checkbox :disabled="isSubmitted" v-model="cvehicle.controled">
              {{ $t('compCarbonFormsVehiclesModal.check-1') }}
              <b-tooltip
                multilined
                :label="$t('compCarbonFormsVehiclesModal.tooltip-1')"
                type="is-black"
              >
                <b-icon icon="info"></b-icon>
              </b-tooltip>
            </b-checkbox>
          </b-notification>
          <b-notification :closable="false">
            <div class="columns">
              <div class="column">
                <b-field
                  v-if="allVehicles.length === 0"
                  :label="$t('id')"
                  :type="{ 'is-danger': errors.has('name') }"
                  :message="errors.first('name')"
                >
                  <b-input
                    name="name"
                    :icon="typeIcon()"
                    :icon-pack="typeIconPack()"
                    v-validate="'required|uniqueID'"
                    :disabled="isSubmitted"
                    maxlength="50"
                    v-model="cvehicle.name"
                  ></b-input>
                </b-field>
                <b-field
                  v-else
                  :label="$t('id')"
                  :type="{ 'is-danger': errors.has('name') }"
                  :message="errors.first('name')"
                >
                  <b-autocomplete
                    :icon="typeIcon()"
                    :icon-pack="typeIconPack()"
                    v-model="cvehicle.name"
                    :open-on-focus="true"
                    v-validate="'required|uniqueID'"
                    :data="allVehicles"
                    field="name"
                    :disabled="isSubmitted"
                    @typing="reset()"
                    @select="(option) => selectedVehicle(option)"
                  >
                  </b-autocomplete>
                </b-field>
              </div>
              <div class="column">
                <b-field
                  :label="$t('type')"
                  :type="{ 'is-danger': errors.has('type') }"
                  :message="errors.first('type')"
                >
                  <b-select
                    expanded
                    @input="typeChanged"
                    name="type"
                    v-validate="'required'"
                    :disabled="isSubmitted"
                    v-model="cvehicle.type"
                  >
                    <option
                      :value="ctype"
                      v-for="(ctype, index) in types"
                      :key="index"
                    >
                      {{ $t(ctype) }}
                    </option>
                  </b-select>
                </b-field>
              </div>
              <div class="column">
                <b-field
                  :label="labelType"
                  :type="{ 'is-danger': errors.has('engine') }"
                  :message="errors.first('engine')"
                >
                  <b-select
                    expanded
                    @input="engineChanged"
                    :disabled="engineDisabled || isSubmitted"
                    name="engine"
                    v-model="cvehicle.engine"
                  >
                    <option
                      :value="cengine"
                      v-for="(cengine, index) in engines"
                      :key="index"
                    >
                      {{ $t(cengine) }}
                    </option>
                  </b-select>
                </b-field>
              </div>
              <div class="column" v-if="cvehicle.engine.startsWith('plane')">
                <b-field
                  :label="$t('compCarbonFormsVehiclesModal.field-7')"
                  :type="{ 'is-danger': errors.has('power') }"
                  :message="errors.first('power')"
                >
                  <b-input
                    name="power"
                    v-validate="'required|numeric'"
                    :disabled="isSubmitted"
                    maxlength="50"
                    v-model="cvehicle.power"
                  ></b-input>
                </b-field>
              </div>
              <div
                class="column"
                v-if="cvehicle.engine.startsWith('helicopter')"
              >
                <b-field
                  :label="$t('compCarbonFormsVehiclesModal.field-8')"
                  :type="{ 'is-danger': errors.has('noEngine') }"
                  :message="errors.first('noEngine')"
                >
                  <b-select
                    :disabled="
                      cvehicle.engine === 'helicopter.pistons' || isSubmitted
                    "
                    expanded
                    name="noEngine"
                    v-validate="'required|numeric'"
                    v-model="cvehicle.noEngine"
                  >
                    <option value="1">1</option>
                    <option value="2">2</option>
                    <option value="3">3</option>
                  </b-select>
                </b-field>
              </div>
              <div
                class="column"
                v-if="cvehicle.engine.startsWith('helicopter')"
              >
                <b-field
                  label="Shaft power"
                  :type="{ 'is-danger': errors.has('shp') }"
                  :message="errors.first('shp')"
                >
                  <b-input
                    name="noEngine"
                    :disabled="isSubmitted"
                    v-validate="'required|numeric'"
                    maxlength="50"
                    v-model="cvehicle.shp"
                  ></b-input>
                </b-field>
              </div>
            </div>
          </b-notification>
          <b-notification :closable="false">
            <consumption-form
              :disabled="isSubmitted"
              v-model="cvehicle.consumption"
            >
              <template v-if="cvehicle.type === 'aircraft'" v-slot:monthly>
                {{ $t('compCarbonFormsVehiclesModal.field-1') }}
              </template>
              <template
                v-else-if="vehiclesWithKM.includes(cvehicle.type)"
                v-slot:monthly
              >
                {{ $t('compCarbonFormsVehiclesModal.field-2') }}
                {{ $t(cvehicle.unit) }}
              </template>
              <template v-else v-slot:monthly>
                {{ $t('compCarbonFormsVehiclesModal.field-3') }}
                {{ $t(cvehicle.unit) }}
              </template>
              <template v-if="cvehicle.type === 'aircraft'" v-slot:total>
                {{ $t('compCarbonFormsVehiclesModal.field-4') }}
              </template>
              <template
                v-else-if="vehiclesWithKM.includes(cvehicle.type)"
                v-slot:total
              >
                {{ $t('compCarbonFormsVehiclesModal.field-5') }}
                {{ $t(cvehicle.unit) }}
              </template>
              <template v-else v-slot:total>
                {{ $t('compCarbonFormsVehiclesModal.field-6') }}
                {{ $t(cvehicle.unit) }}
              </template>
            </consumption-form>
          </b-notification>
          <b-notification v-if="authStore.isAuthenticated" :closable="false">
            <tags-input v-model="cvehicle.tags" :disabled="isSubmitted" />
          </b-notification>
          <hr />
          <nav class="level">
            <div class="level-left"></div>
            <div class="buttons level-right">
              <b-button
                class="button"
                @click="$emit('close')"
                icon-left="close"
              >
                {{ $t('cancel') }}
              </b-button>
              <button
                v-show="modeCreation"
                type="submit"
                :disabled="isSubmitted"
                class="button is-primary"
              >
                <span class="icon is-small"><i class="fa fa-plus"></i></span>
                <span> {{ $t('add') }} </span>
              </button>
              <button
                v-show="!modeCreation"
                :disabled="isSubmitted"
                type="submit"
                class="button is-primary"
              >
                <span class="icon is-small"><i class="fa fa-refresh"></i></span>
                <span> {{ $t('update') }} </span>
              </button>
            </div>
          </nav>
        </section>
      </form>
    </div>
  </b-modal>
</template>

<script>
import Vehicle, {
  DEFAULT_ENGINE,
  DEFAULT_NOENGINE,
  DEFAULT_TYPE,
  DEFAULT_UNIT
} from '@/models/carbon/Vehicle.js'
import { useAdminStore } from '@/stores/admin.js'
import { useGHGIStore } from '@/stores/ghgi.js'
import { useAuthenticationStore } from '@/stores/authentication.js'

import _ from 'lodash'

export default {
  name: 'Modal',
  props: {
    displayModal: {
      type: Boolean,
      default: false
    },
    vehicle: {
      type: Object,
      default: function () {
        return {}
      }
    },
    vehicles: {
      type: Array,
      default: function () {
        return []
      }
    },
    modeCreation: {
      type: Boolean,
      default: false
    }
  },
  components: {
    'tags-input': () => import('@/components/core/TagsInput.vue'),
    'consumption-form': () =>
      import('@/components/carbon/forms/ConsumptionForm.vue')
  },
  $_veeValidate: {
    validator: 'new'
  },
  data () {
    return {
      cvehicle: _.cloneDeep(this.vehicle),
      vehiclesWithKM: ['car', 'motorbike', 'bike', 'scooter']
    }
  },
  setup () {
    const adminStore = useAdminStore()
    const authStore = useAuthenticationStore()
    const ghgiStore = useGHGIStore()
    return { adminStore, authStore, ghgiStore }
  },
  computed: {
    allVehicles: function () {
      let usedNames = this.vehicles.map((obj) => obj.name)
      return this.adminStore.allVehicles.filter(
        (obj) => !usedNames.includes(obj.name)
      )
    },
    labelType: function () {
      if (this.cvehicle.engine.startsWith('helicopter')) {
        return this.$t('compCarbonFormsVehiclesModal.field-9')
      } else if (this.cvehicle.engine.startsWith('plane')) {
        return this.$t('compCarbonFormsVehiclesModal.field-9')
      } else {
        return this.$t('engine')
      }
    },
    isSubmitted: function () {
      return this.ghgiStore.item.isSubmitted
    },
    types: function () {
      return Vehicle.getTypes()
    },
    engines: function () {
      if (this.cvehicle.type) {
        return Vehicle.getEngines(this.cvehicle.type)
      } else {
        return ''
      }
    },
    enginesUnits: function () {
      return Vehicle.getUnits(this.cvehicle.type, this.cvehicle.engine)
    },
    engineDisabled: function () {
      return this.engines.length === 1
    },
    modalTitle: function () {
      let title = this.$t('compCarbonFormsVehiclesModal.title-1')
      if (this.modeCreation) {
        title = this.$t('compCarbonFormsVehiclesModal.title-2')
      } else if (this.isSubmitted) {
        title = this.$t('compCarbonFormsVehiclesModal.title-3')
      }
      return title
    }
  },
  methods: {
    typeIcon: function () {
      return Vehicle.getIcon(this.cvehicle.type)
    },
    typeIconPack: function () {
      return Vehicle.getIconPack(this.cvehicle.type)
    },
    validateBeforeSubmit () {
      this.$validator.validateAll().then((result) => {
        if (result) {
          if (this.modeCreation) {
            this.$emit('add', this.cvehicle)
          } else {
            this.$emit('update', {
              from: this.vehicle,
              to: this.cvehicle
            })
          }
        }
      })
    },
    typeChanged: function () {
      this.cvehicle.engine = this.engines[0]
      this.cvehicle.unit = this.enginesUnits
    },
    engineChanged: function () {
      this.cvehicle.unit = this.enginesUnits
      if (this.cvehicle.engine === 'helicopter.pistons') {
        this.cvehicle.noEngine = 1
      }
    },
    selectedVehicle (vehicle) {
      this.cvehicle.id = vehicle.id
      this.cvehicle.name = vehicle.name
      this.cvehicle.type = vehicle.type
      this.cvehicle.engine = vehicle.engine
      this.cvehicle.unit = vehicle.unit
      this.cvehicle.power = vehicle.power
      this.cvehicle.noEngine = vehicle.noEngine
      this.cvehicle.shp = vehicle.shp
      this.cvehicle.controled = vehicle.controled
    },
    reset () {
      this.cvehicle.id = null
      this.cvehicle.name = ''
      this.cvehicle.type = DEFAULT_TYPE
      this.cvehicle.engine = DEFAULT_ENGINE
      this.cvehicle.unit = DEFAULT_UNIT
      this.cvehicle.power = 0
      this.cvehicle.noEngine = DEFAULT_NOENGINE
      this.cvehicle.shp = 0
      this.cvehicle.controled = true
    }
  },
  created () {
    this.$validator.extend('uniqueID', {
      getMessage: (field) => 'Identifiant déjà utilisé.',
      validate: (value) => {
        if (!this.modeCreation) {
          if (value === this.cvehicle.name) {
            return true
          } else {
            return (
              this.vehicles.filter((obj) => obj.name === value).length === 0
            )
          }
        } else {
          return this.vehicles.filter((obj) => obj.name === value).length === 0
        }
      }
    })
  }
}
</script>
